import { interpolateFrame } from "./frames";
import { buildSvgString } from "./svgString";
import { DEFAULT_FRAME_DURATION } from "./constants";

/**
 * Export a sequence of tactical frames as an animated GIF.
 * @returns {Promise<Blob>} the GIF binary blob.
 */
export async function exportFramesToGif(frames, { width = 600, height = 600, fps = 20 } = {}) {
  const GIFmod = await import("gif.js");
  const GIF = GIFmod.default;
  const intervalMs = 1000 / fps;
  const gif = new GIF({
    workers: 2,
    quality: 10,
    width,
    height,
    workerScript: "/gif.worker.js",
    background: "#0d6e3f",
  });

  const renderToCanvas = async (snap) => {
    const svgString = buildSvgString(snap);
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      return canvas;
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  for (let i = 0; i < frames.length - 1; i++) {
    const fa = frames[i];
    const fb = frames[i + 1];
    const dur = fa.duration_ms || DEFAULT_FRAME_DURATION;
    const steps = Math.max(2, Math.round(dur / intervalMs));
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const snap = interpolateFrame(fa, fb, t);
      // eslint-disable-next-line no-await-in-loop
      const c = await renderToCanvas(snap);
      gif.addFrame(c, { delay: intervalMs });
    }
  }
  // Final frame for 600ms
  const last = await renderToCanvas(frames[frames.length - 1]);
  for (let k = 0; k < Math.round(600 / intervalMs); k++) {
    gif.addFrame(last, { delay: intervalMs, copy: true });
  }

  return new Promise((resolve, reject) => {
    gif.on("finished", resolve);
    gif.on("abort", () => reject(new Error("aborted")));
    gif.render();
  });
}
